import { useEffect, useRef, type ReactNode } from "react";

interface DrawerProps {
  open: boolean;
  title: string;
  onClose: () => void;
  closeLabel?: string;
  className?: string;
  headerActions?: ReactNode;
  children: ReactNode;
}

/**
 * Right-anchored side panel with dialog semantics: labelled, Esc closes,
 * focus moves to the panel on open and returns to the opener on close.
 * Not modal — the page behind stays interactive, so no focus trap.
 */
export function Drawer({ open, title, onClose, closeLabel = `Close ${title.toLowerCase()}`, className = "", headerActions, children }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);
  const titleId = `drawer-title-${title.replace(/\s+/g, "-").toLowerCase()}`;

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus({ preventScroll: true });
    };
  }, [open, onClose]);

  return (
    <div
      ref={panelRef}
      className={`alert-drawer ${className} ${open ? "alert-drawer--open" : ""}`}
      role="dialog"
      aria-labelledby={titleId}
      aria-hidden={!open}
      tabIndex={-1}
    >
      {/* Contents are unmounted while closed so the hidden panel has no
          focusable descendants; the container stays for the slide transition. */}
      {open && (
        <>
          <div className="alert-drawer__header">
            <span id={titleId} className="alert-drawer__title">{title}</span>
            {headerActions}
            <button className="alert-drawer__close" onClick={onClose} aria-label={closeLabel} type="button">
              ✕
            </button>
          </div>
          <div className="alert-drawer__body">{children}</div>
        </>
      )}
    </div>
  );
}
